import { useState, useEffect, ReactNode } from 'react';
import { useRouter } from 'next/router';
import dynamic from 'next/dynamic';
import { useQuery } from 'react-query';
import {
  EntityTypes,
  FormTypes,
  SearchViewTypes
} from '../../../../new-lib/constants/common';
import { getPropertyTypes } from '../../../../new-lib/apis';
import { useContactForm } from '../../hooks/ContactForm';
import ContactFormPresentation from './ContactFormPresentation';
import { logEvent } from '../../../../new-lib/analytics';
import { useSelector } from 'react-redux';
import { trackEvent } from '../../../../new-lib/remarketing';
import { ListItemType } from './list-item.type';
import { CompoundsApis } from '../../../../new-lib/apis/compounds.apis';
import { useDebounce } from '../../../../hooks/debounce.hooks';
import { AreasApis } from '../../../../new-lib/apis/areas.apis';

const SuccessModal = dynamic(() => import('../SuccessModal'), { ssr: false });

interface FormProps {
  formSource?: FormTypes;
  location?: {
    areaId?: string;
    developerId?: string;
    compoundId?: string;
    propertyId?: string;
  };
  isContactPage?: boolean;
  customEventName?: string;
  renderedInputs?: {
    name?: boolean;
    location?: boolean;
    mobileNumber?: boolean;
    message?: boolean;
    email?: boolean;
    buyerOrSeller?: boolean;
  };
  withLabels?: boolean;
  layout?: {
    header?: ReactNode;
    footer?: ReactNode;
  };
  onSubmit?: () => void;
  forecastFlag?: boolean;
  organizationId?: number | null;
  landingPageId?: number;
  compound?: {
    id: number;
    name: string;
    nawy_organization_id: number | null;
    area?: { id: string; name: string };
    developer?: { id: string; name: string };
    min_price?: number;
    max_price?: number;
    dev_price?: number;
    min_unit_area?: number;
    max_unit_area?: number;
    finishing_type?: string;
    no_beds?: number;
    no_baths?: number;
    dwn_pymnt_prcnt?: number;
    install_yrs?: number;
    is_delivered?: boolean;
    delivery_date?: string;
  };
  isNewLandingPage?: boolean;
}

const ContactFormContainer: React.FC<FormProps> = ({
  formSource,
  location,
  isContactPage,
  renderedInputs,
  withLabels,
  layout,
  onSubmit,
  customEventName,
  forecastFlag,
  organizationId,
  landingPageId,
  compound,
  isNewLandingPage
}) => {
  const { locale, query } = useRouter();
  const [isFocused, setIsFocused] = useState(false); // Track if dropdown is focused
  const [searchInput, setSearchInput] = useState('');
  const debouncedSearch = useDebounce(searchInput, 100);

  const { data: compoundsList = [], isFetching } = useQuery({
    queryKey: ['compounds-autocomplete', locale, debouncedSearch],
    queryFn: async ({ signal }) =>
      CompoundsApis.autocomplete(locale, debouncedSearch, { signal }).then(
        results =>
          results?.map((compound: any) => ({
            value: compound.id,
            label: compound.name,
            nawy_organization_id: compound.nawyOrganizationId
          })) ?? []
      ),
    enabled: isFocused || !!compound?.name,
    staleTime: Infinity
  });

  const { data: propertyTypesList } = useQuery(
    ['property_types', locale],
    () =>
      getPropertyTypes(locale).then(
        items =>
          items?.map((item: any) => ({
            value: item.id,
            label: item.name
          })) ?? []
      ),
    { enabled: renderedInputs?.buyerOrSeller ?? false, staleTime: Infinity }
  );

  const { data: areasList = [] } = useQuery(
    ['areas', locale],
    () =>
      AreasApis.listAll(locale).then(
        items =>
          items.map(
            (item): ListItemType => ({
              value: `${item.value}`,
              label: item.options.displayedValue
            })
          ) ?? []
      ),
    { enabled: renderedInputs?.buyerOrSeller ?? false, staleTime: Infinity }
  );

  const selectedCompound = useSelector(
    (state: any) => state.map.selectedCompound
  );

  const [successModal, setSuccessModal] = useState<boolean>(false);
  const eventId = useSelector((state: any) => state.app.eventId);

  const onFormSubmit = () => {
    if (onSubmit) {
      onSubmit();
    }

    setSuccessModal(true);
    setTimeout(() => setSuccessModal(false), 2000);
    logEvent.contact(
      'form_general',
      customEventName
        ? customEventName
        : isContactPage
          ? 'ContactPage'
          : 'FormAtEndOfPage',
      eventId,
      {
        entity_type: compound?.dev_price
          ? EntityTypes.COMPOUND
          : EntityTypes.PROPERTY,
        ...(contactFormData.formik.values.compound.value && {
          cmpnd_id: parseInt(contactFormData.formik.values.compound.value),
          cmpnd_name: contactFormData.formik.values.compound.label
        }),
        ...((contactFormData.formik.values.area.value || compound?.area) && {
          area_id: parseInt(
            contactFormData.formik.values.area.value || compound?.area?.id!
          ),
          area_name:
            contactFormData.formik.values.area.label || compound?.area?.name
        }),
        ...(compound?.developer && {
          dev_id: parseInt(compound?.developer?.id!),
          dev_name: compound?.developer?.name
        }),
        ...(contactFormData.formik.values.compound &&
          forecastFlag && { forecast_available: forecastFlag }),
        ...(compound?.min_price && { min_price: compound?.min_price }),
        ...(compound?.max_price && { max_price: compound?.max_price }),
        ...(compound?.dev_price && { dev_price: compound?.dev_price }),
        ...(compound?.min_unit_area && {
          min_unit_area: compound?.min_unit_area
        }),
        ...(compound?.max_unit_area && {
          max_unit_area: compound?.max_unit_area
        }),
        ...(compound?.no_beds && { no_beds: compound?.no_beds }),
        ...(compound?.no_baths && { no_baths: compound?.no_baths }),
        ...(compound?.dwn_pymnt_prcnt && {
          dwn_pymnt_prcnt: compound?.dwn_pymnt_prcnt
        }),
        ...(compound?.install_yrs && { install_yrs: compound?.install_yrs }),
        ...(compound?.is_delivered !== undefined && {
          is_delivered: compound?.is_delivered
        })
      }
    );
    if (location?.compoundId != undefined || location?.propertyId != undefined)
      trackEvent.purchase({
        listingIds: [parseInt(location.compoundId || location.propertyId || '')]
      });
  };

  const handleListItemSelection = (
    listName: string,
    selectedItem: ListItemType
  ) => {
    contactFormData.formik.setFieldValue(listName, selectedItem);
  };

  const handleBuyerOrSeller = (seller: boolean) => {
    contactFormData.formik.setFieldValue('seller', false);
    contactFormData.handleResetForm({
      compound: contactFormData.formik.values.compound,
      area: contactFormData.formik.values.area,
      seller: seller
    });
  };

  useEffect(() => {
    if (compound?.name) {
      setSearchInput(compound?.name);
    }

    if (query?.view === SearchViewTypes.MAP && selectedCompound) {
      setSearchInput(selectedCompound?.name);
    }
  }, [compound?.name]);

  useEffect(() => {
    if (compound?.name && !contactFormData.formik.values.compound?.value) {
      const selectedCompound = compoundsList.find(
        (cmpd: ListItemType) => cmpd.label == compound?.name
      );

      if (selectedCompound) {
        contactFormData.formik.setFieldValue('compound', selectedCompound);
      }

      if (renderedInputs?.buyerOrSeller && compound.area) {
        contactFormData.formik.setFieldValue('area', {
          label: compound.area.name,
          value: compound.area.id
        });
      }
    }
  }, [compoundsList, compound?.name]);

  useEffect(() => {
    contactFormData.formik.setFieldValue('compound', '');
  }, [query.compound_slug]);

  const contactFormData = useContactForm({
    onFormSubmit,
    formSource: formSource ?? FormTypes.ORGANIC_FORM,
    location: location,
    landingPageId
  });
  return (
    <>
      <ContactFormPresentation
        {...contactFormData}
        handleCompoundSearchInput={input => setSearchInput(input)}
        handleListItemSelection={handleListItemSelection}
        handleBuyerOrSeller={handleBuyerOrSeller}
        compounds={compoundsList}
        isLoadingCompounds={isFetching}
        areas={areasList}
        propertyTypes={propertyTypesList}
        renderedInputs={renderedInputs}
        withLabels={withLabels}
        layout={layout}
        onCompoundFocus={() => setIsFocused(true)}
        isNewLandingPage={isNewLandingPage}
      />
      {successModal && <SuccessModal />}
    </>
  );
};

export default ContactFormContainer;
